<template>
  <div>
    <v-progress-linear v-if="cargando" indeterminate color="primary" class="mb-3" />

    <v-alert v-if="!cargando && items.length === 0" type="info" dense text>
      No se encontraron listas de precio.
    </v-alert>

    <v-card v-for="(item, idx) in items" :key="idx" outlined class="mb-4">
      <v-card-title class="py-2 px-4 grey lighten-4 d-flex align-center">
        <v-icon small color="primary" class="mr-2">mdi-format-list-bulleted-type</v-icon>
        <span class="text-subtitle-2 font-weight-bold">{{ item.tipoListaPrecio.nombre }}</span>
        <v-chip small color="primary" dark class="ml-3">{{ item.categoriaUniversidad.nombre }}</v-chip>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0" style="overflow-x: auto">
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <!-- Fila 1: grupos -->
              <tr>
                <th rowspan="2" class="grey lighten-3 text-center" style="vertical-align: middle">ÁREA</th>

                <!-- Conceptos (un solo bloque, datos iguales en ambas modalidades) -->
                <template v-for="(concepto, cIdx) in obtenerConceptosUnicos(item)">
                  <th
                    :key="'h1-' + cIdx"
                    colspan="3"
                    class="text-center"
                    :class="cIdx % 2 === 0 ? 'amber lighten-4' : 'orange lighten-4'">
                    {{ concepto }}
                  </th>
                </template>

                <!-- Resumen CONTADO -->
                <th
                  :colspan="2 + obtenerConceptosUnicos(item).length"
                  class="text-center green lighten-4 green--text text--darken-2">
                  CONTADO
                </th>

                <!-- Resumen CRÉDITO -->
                <th
                  :colspan="2 + obtenerConceptosUnicos(item).length"
                  class="text-center blue lighten-4 blue--text text--darken-2">
                  CRÉDITO
                </th>
              </tr>

              <!-- Fila 2: sub-headers -->
              <tr>
                <!-- Sub-headers conceptos -->
                <template v-for="(concepto, cIdx) in obtenerConceptosUnicos(item)">
                  <th
                    :key="'h2-m-' + cIdx"
                    class="text-right text-caption"
                    :class="cIdx % 2 === 0 ? 'amber lighten-5' : 'orange lighten-5'">
                    Monto
                  </th>
                  <th
                    :key="'h2-c-' + cIdx"
                    class="text-right text-caption"
                    :class="cIdx % 2 === 0 ? 'amber lighten-5' : 'orange lighten-5'">
                    N° Cuotas / Módulos
                  </th>
                  <th
                    :key="'h2-t-' + cIdx"
                    class="text-right text-caption"
                    :class="cIdx % 2 === 0 ? 'amber lighten-5' : 'orange lighten-5'">
                    Total
                  </th>
                </template>

                <!-- Sub-headers resumen CONTADO -->
                <th class="text-right text-caption green lighten-5">Plan</th>
                <template v-for="(concepto, cIdx) in obtenerConceptosUnicos(item)">
                  <th :key="'contado-h2-d-' + cIdx" class="text-right text-caption green lighten-5">
                    % Desc. {{ concepto }}
                  </th>
                </template>
                <th class="text-right text-caption green lighten-5 font-weight-bold">Precio Mín.</th>

                <!-- Sub-headers resumen CRÉDITO -->
                <th class="text-right text-caption blue lighten-5">Plan</th>
                <template v-for="(concepto, cIdx) in obtenerConceptosUnicos(item)">
                  <th :key="'credito-h2-d-' + cIdx" class="text-right text-caption blue lighten-5">
                    % Desc. {{ concepto }}
                  </th>
                </template>
                <th class="text-right text-caption blue lighten-5 font-weight-bold">Precio Mín.</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(area, aIdx) in item.areas" :key="aIdx">
                <td class="font-weight-medium" style="min-width: 200px">
                  {{ area.areaUniversidad.nombre }}
                  <v-chip x-small outlined class="ml-1">{{
                    modalidadesProgramasUniversidadesLabels[area.modalidad]
                  }}</v-chip>
                </td>

                <!-- Datos conceptos (tomados de cualquier modalidad disponible) -->
                <template v-for="(concepto, cIdx) in obtenerConceptosUnicos(item)">
                  <td :key="'m-' + aIdx + '-' + cIdx" class="text-right">
                    {{ obtenerDatosConcepto(area, concepto).montoCuota }}
                  </td>
                  <td :key="'c-' + aIdx + '-' + cIdx" class="text-right">
                    {{ obtenerDatosConcepto(area, concepto).cuotas }}
                  </td>
                  <td :key="'t-' + aIdx + '-' + cIdx" class="text-right font-weight-medium">
                    {{ obtenerDatosConcepto(area, concepto).total }}
                  </td>
                </template>

                <!-- Resumen CONTADO -->
                <td class="text-right font-weight-bold green--text text--darken-2">
                  {{ calcularPlanModalidad(area, 'contado') }}
                </td>
                <template v-for="(concepto, cIdx) in obtenerConceptosUnicos(item)">
                  <td :key="'contado-d-' + aIdx + '-' + cIdx" class="text-right">
                    {{
                      obtenerDatosModalidad(area, 'contado', concepto).existe
                        ? obtenerDatosModalidad(area, 'contado', concepto).porcentaje + '%'
                        : '-'
                    }}
                  </td>
                </template>
                <td class="text-right font-weight-bold green--text text--darken-2">
                  {{ calcularPrecioMinModalidad(area, 'contado') }}
                </td>

                <!-- Resumen CRÉDITO -->
                <td class="text-right font-weight-bold blue--text text--darken-2">
                  {{ calcularPlanModalidad(area, 'credito') }}
                </td>
                <template v-for="(concepto, cIdx) in obtenerConceptosUnicos(item)">
                  <td :key="'credito-d-' + aIdx + '-' + cIdx" class="text-right">
                    {{
                      obtenerDatosModalidad(area, 'credito', concepto).existe
                        ? obtenerDatosModalidad(area, 'credito', concepto).porcentaje + '%'
                        : '-'
                    }}
                  </td>
                </template>
                <td class="text-right font-weight-bold blue--text text--darken-2">
                  {{ calcularPrecioMinModalidad(area, 'credito') }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ModalidadesProgramasUniversidadesLabels } from '../../utils/constantes';

export default {
  name: 'TablaPivotListaPrecio',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    cargando: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalidadesProgramasUniversidadesLabels: ModalidadesProgramasUniversidadesLabels,
    };
  },
  methods: {
    obtenerConceptosUnicos(item) {
      const conceptosMap = new Map();

      for (const area of item.areas) {
        for (const mod of area.modalidades) {
          for (const con of mod.conceptos) {
            if (!conceptosMap.has(con.conceptoPago.id)) {
              conceptosMap.set(con.conceptoPago.id, con.conceptoPago.nombre);
            }
          }
        }
      }

      return Array.from(conceptosMap.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, nombre]) => nombre);
    },
    obtenerDatosConcepto(area, conceptoNombre) {
      for (const mod of area.modalidades) {
        const con = mod.conceptos.find((c) => c.conceptoPago.nombre === conceptoNombre);

        if (con) {
          return {
            montoCuota: con.numeroCuotas > 0 ? Math.round((con.monto / con.numeroCuotas) * 100) / 100 : 0,
            cuotas: con.numeroCuotas,
            total: con.monto,
          };
        }
      }

      return { montoCuota: '-', cuotas: '-', total: '-' };
    },
    obtenerDatosModalidad(area, modalidadPago, conceptoNombre) {
      const mod = area.modalidades.find((m) => m.modalidadPago === modalidadPago);

      if (!mod) return { porcentaje: null, existe: false };

      const con = mod.conceptos.find((c) => c.conceptoPago.nombre === conceptoNombre);

      if (!con) return { porcentaje: null, existe: false };

      return { porcentaje: con.porcentajeMaximoDescuento, existe: true };
    },
    calcularPlanModalidad(area, modalidadPago) {
      const mod = area.modalidades.find((m) => m.modalidadPago === modalidadPago);

      if (!mod) return '-';

      return Math.round(mod.conceptos.reduce((sum, con) => sum + Number(con.monto), 0) * 100) / 100;
    },
    calcularPrecioMinModalidad(area, modalidadPago) {
      const mod = area.modalidades.find((m) => m.modalidadPago === modalidadPago);

      if (!mod) return '-';

      return Math.round(mod.conceptos.reduce((sum, con) => sum + Number(con.precioTotalMinimo), 0) * 100) / 100;
    },
  },
};
</script>
